import React, { useEffect, useState } from "react";
import { App, Button, Input, Select, Switch, InputNumber, Divider } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppControl from "../../components/";
import Card from "../../components/controls/Card";
import {
  getServiceCategory,
  saveServiceCategory,
  updateServiceCategory,
} from "../../store/slice/serviceCategories";

const COLOR_OPTIONS = [
  { label: "Orange", value: "orange" },
  { label: "Green", value: "green" },
  { label: "Blue", value: "blue" },
  { label: "Cyan", value: "cyan" },
  { label: "Yellow", value: "yellow" },
  { label: "Teal", value: "teal" },
  { label: "Amber", value: "amber" },
  { label: "Pink", value: "pink" },
  { label: "Purple", value: "purple" },
  { label: "Red", value: "red" },
];

const DEMAND_TIER_OPTIONS = [
  {
    label: "High (1.5× pricing multiplier)",
    value: "high",
    description: "Electrician, Plumber, AC Technician",
  },
  {
    label: "Standard (1.0× pricing multiplier)",
    value: "standard",
    description: "Carpenter, Appliance Repair, Mason",
  },
  {
    label: "Low (0.6× pricing multiplier)",
    value: "low",
    description: "Locksmith, Upholsterer, Glass Fitter",
  },
];

const validationSchema = Yup.object().shape({
  key: Yup.string().required("Key is required"),
  label: Yup.string().required("Label is required"),
  icon: Yup.string().required("Icon is required"),
  color: Yup.string().required("Color is required"),
  demandTier: Yup.string().required("Demand tier is required"),
  sortOrder: Yup.number().required("Sort order is required").min(1),
});

function ServiceCategoryAddEdit() {
  const [title, setTitle] = useState("CREATE SERVICE CATEGORY");
  const { id } = useParams();
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { category, isLoading } = useSelector(
    (state) => state.serviceCategories,
  );

  useEffect(() => {
    if (id) {
      setTitle("EDIT SERVICE CATEGORY");
      dispatch(getServiceCategory(id));
    }
  }, [id, dispatch]);

  const initialValues =
    id && category
      ? {
          key: category.key || "",
          label: category.label || "",
          icon: category.icon || "",
          color: category.color || "",
          demandTier: category.demandTier || "standard",
          isPrimary: category.isPrimary || false,
          sortOrder: category.sortOrder || 1,
          isActive: category.isActive !== undefined ? category.isActive : true,
        }
      : {
          key: "",
          label: "",
          icon: "",
          color: "",
          demandTier: "standard",
          isPrimary: false,
          sortOrder: 1,
          isActive: true,
        };

  const onSubmit = async (values) => {
    const thunkCreator = id ? updateServiceCategory : saveServiceCategory;
    const payload = id ? { ...values, id } : values;
    const resultAction = await dispatch(thunkCreator(payload));

    if (thunkCreator.fulfilled.match(resultAction)) {
      message.success(resultAction.payload.message);
      navigate("/service-categories");
    } else {
      message.error(resultAction.error.message);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Card title={title}>
          <Form>
            <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("key")}
                  placeholder="e.g. electrician"
                  status={
                    formik.touched.key && formik.errors.key ? "error" : ""
                  }
                />
                {formik.touched.key && formik.errors.key && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.key}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("label")}
                  placeholder="e.g. Electrician"
                  status={
                    formik.touched.label && formik.errors.label ? "error" : ""
                  }
                />
                {formik.touched.label && formik.errors.label && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.label}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon <span className="text-red-500">*</span>
                </label>
                <Input
                  {...formik.getFieldProps("icon")}
                  placeholder="e.g. ZapIcon, wrench, camera"
                  status={
                    formik.touched.icon && formik.errors.icon ? "error" : ""
                  }
                />
                {formik.touched.icon && formik.errors.icon && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.icon}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color <span className="text-red-500">*</span>
                </label>
                <Select
                  {...formik.getFieldProps("color")}
                  value={formik.values.color || undefined}
                  onChange={(value) => formik.setFieldValue("color", value)}
                  options={COLOR_OPTIONS}
                  placeholder="Select color"
                  status={
                    formik.touched.color && formik.errors.color ? "error" : ""
                  }
                />
                {formik.touched.color && formik.errors.color && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.color}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Demand Tier <span className="text-red-500">*</span>
                </label>
                <Select
                  {...formik.getFieldProps("demandTier")}
                  value={formik.values.demandTier || undefined}
                  onChange={(value) => formik.setFieldValue("demandTier", value)}
                  options={DEMAND_TIER_OPTIONS}
                  placeholder="Select demand tier"
                  status={
                    formik.touched.demandTier && formik.errors.demandTier ? "error" : ""
                  }
                />
                {formik.touched.demandTier && formik.errors.demandTier && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.demandTier}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sort Order <span className="text-red-500">*</span>
                </label>
                <InputNumber
                  {...formik.getFieldProps("sortOrder")}
                  value={formik.values.sortOrder}
                  onChange={(value) => formik.setFieldValue("sortOrder", value)}
                  className="w-full"
                  min={1}
                  status={
                    formik.touched.sortOrder && formik.errors.sortOrder
                      ? "error"
                      : ""
                  }
                />
                {formik.touched.sortOrder && formik.errors.sortOrder && (
                  <p className="text-red-500 text-xs mt-1">
                    {formik.errors.sortOrder}
                  </p>
                )}
              </div>
            </div>

            <Divider titlePlacement="left" plain>
              <span className="text-sm font-medium text-gray-600">
                Settings
              </span>
            </Divider>

            <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-6 mb-6">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                <label className="text-sm font-medium text-gray-700">
                  Is Primary
                </label>
                <Switch
                  checked={formik.values.isPrimary}
                  onChange={(value) => formik.setFieldValue("isPrimary", value)}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                <label className="text-sm font-medium text-gray-700">
                  Is Active
                </label>
                <Switch
                  checked={formik.values.isActive}
                  onChange={(value) => formik.setFieldValue("isActive", value)}
                />
              </div>
            </div>

            <div className="border-t-2 border-neutral-100 pt-4 mt-6">
              <AppControl
                control="button"
                type="submit"
                name="Save"
                size="md"
                color="orange"
                disabled={!formik.isValid || isLoading}
                loading={isLoading}
              />
              <AppControl
                control="button"
                type="button"
                name="Cancel"
                size="md"
                color="blue"
                onClick={() => navigate("/service-categories")}
              />
            </div>
          </Form>
        </Card>
      )}
    </Formik>
  );
}

export default ServiceCategoryAddEdit;
